import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import LoginModal from "../components/LoginModal";
import AddStockModal from "../components/AddStockModal";
import ScanStocksModal from "../components/ScanStocksModal";
import StockNewsModal from "../components/StockNewsModal";
import StockRow from "../components/StockRow";

function StocksPage() {
  const { token, user, isAuthed, booting, logout } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [newsSymbol, setNewsSymbol] = useState(null);
  const [newsCounts, setNewsCounts] = useState({});
  const [tab, setTab] = useState("holding");

  const fetchStocks = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/api/stocks", { token });
      setStocks(data.stocks || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // One call for every badge, rather than one per row.
  const fetchNewsCounts = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch("/api/news/counts", { token });
      setNewsCounts(data.counts || {});
    } catch {
      // Badges are decoration — a failure here must not break the list.
    }
  }, [token]);

  useEffect(() => {
    fetchStocks();
    fetchNewsCounts();
  }, [fetchStocks, fetchNewsCounts]);

  // The collector runs in the background, so refresh counts periodically.
  useEffect(() => {
    if (!token) return undefined;
    const interval = setInterval(fetchNewsCounts, 120000);
    return () => clearInterval(interval);
  }, [token, fetchNewsCounts]);

  // Adding a stock starts a one-month backfill on the server, which takes a
  // minute or two. Check back a few times so the badge appears on its own
  // rather than after the next two-minute tick.
  const backfillTimers = useRef([]);
  const watchForBackfill = useCallback(() => {
    backfillTimers.current.forEach(clearTimeout);
    backfillTimers.current = [20000, 45000, 90000, 180000].map((ms) =>
      setTimeout(fetchNewsCounts, ms)
    );
  }, [fetchNewsCounts]);
  useEffect(() => () => backfillTimers.current.forEach(clearTimeout), []);

  const addStock = async (payload) => {
    const data = await apiFetch("/api/stocks", { method: "POST", token, body: payload });
    setStocks((prev) => [data.stock, ...prev]);
    watchForBackfill();
  };

  // Moving between lists is the same PATCH as any other edit — the collector
  // reads watch_status straight off the stock.
  const moveStock = async (id, watch_status) => {
    const data = await apiFetch(`/api/stocks/${id}`, {
      method: "PATCH",
      token,
      body: { watch_status },
    });
    setStocks((prev) => prev.map((s) => (s.id === id ? data.stock : s)));
  };

  const saveStock = async (id, patch) => {
    const data = await apiFetch(`/api/stocks/${id}`, { method: "PATCH", token, body: patch });
    setStocks((prev) => prev.map((s) => (s.id === id ? data.stock : s)));
  };

  const deleteStock = async (id) => {
    await apiFetch(`/api/stocks/${id}`, { method: "DELETE", token });
    setStocks((prev) => prev.filter((s) => s.id !== id));
  };

  const scanImage = (base64, mimeType) =>
    apiFetch("/api/stocks/scan", {
      method: "POST",
      token,
      body: { image_base64: base64, mime_type: mimeType },
    });

  const bulkAdd = async (items) => {
    const result = await apiFetch("/api/stocks/bulk", {
      method: "POST",
      token,
      body: { stocks: items },
    });
    await fetchStocks();
    watchForBackfill();
    return result;
  };

  const visible = stocks.filter((s) => (s.watch_status || "holding") === tab);

  return (
    <div className="app">
      <header className="header">
        <div className="header-inner">
          <div className="header-title-row">
            <div className="logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
            </div>
            <div>
              <h1>מעקב מניות</h1>
              <p className="header-subtitle">
                {isAuthed
                  ? `${stocks.length} מניות ברשימה שלך`
                  : "רשימה פרטית לכל משתמש"}
              </p>
            </div>
          </div>
          <div className="header-actions">
            <Link to="/" className="nav-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>הודעות</span>
            </Link>
            {isAuthed && (
              <button className="nav-link" onClick={logout} title={user?.phone || ""}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>התנתק</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="main">
        {!isAuthed ? (
          booting ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <div className="empty-state auth-gate">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <h2>הרשימה שלך פרטית</h2>
              <p>התחבר עם מספר הטלפון שלך כדי לראות ולנהל את המניות שלך</p>
              <button className="subscribe-btn" onClick={() => setShowLogin(true)}>
                התחבר
              </button>
            </div>
          )
        ) : (
          <>
            <div className="stocks-toolbar">
              <button className="subscribe-btn" onClick={() => setShowAdd(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>הוסף מניה</span>
              </button>
              <button className="btn-secondary" onClick={() => setShowScan(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>סרוק תמונה</span>
              </button>
            </div>

            <div className="stock-tabs" role="tablist">
              {[
                { id: "holding", label: "המניות שלי" },
                { id: "considering_sell", label: "שוקל למכור" },
              ].map((t) => {
                const n = stocks.filter(
                  (s) => (s.watch_status || "holding") === t.id
                ).length;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`stock-tab${tab === t.id ? " active" : ""}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                    <span className="tab-count">{n}</span>
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="error-banner">
                <span>⚠ {error}</span>
                <button onClick={fetchStocks}>נסה שוב</button>
              </div>
            )}

            {loading && stocks.length === 0 ? (
              <div className="loader-container">
                <div className="loader" />
                <p>טוען מניות...</p>
              </div>
            ) : visible.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                <h2>{tab === "considering_sell" ? "אין מניות בשקילה" : "אין מניות ברשימה"}</h2>
                <p>
                  {tab === "considering_sell"
                    ? "העבר לכאן מניה מהרשימה כדי לעקוב אחריה מקרוב יותר"
                    : "הוסף מניה ידנית או סרוק צילום מסך של התיק שלך"}
                </p>
              </div>
            ) : (
              <div className="stock-list">
                {visible.map((s) => (
                  <StockRow
                    key={s.id}
                    stock={s}
                    onSave={saveStock}
                    onDelete={deleteStock}
                    newsCount={newsCounts[s.symbol]?.count || 0}
                    signal={newsCounts[s.symbol]?.signal || "neutral"}
                    strength={newsCounts[s.symbol]?.strength || 0}
                    bullish={newsCounts[s.symbol]?.bullish || 0}
                    bearish={newsCounts[s.symbol]?.bearish || 0}
                    score={newsCounts[s.symbol]?.score || null}
                    onOpenNews={setNewsSymbol}
                    onMove={moveStock}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <footer className="footer">
        <p>מעקב מניות · רשימה פרטית לכל משתמש</p>
      </footer>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showAdd && <AddStockModal onClose={() => setShowAdd(false)} onAdd={addStock} />}
      {showScan && (
        <ScanStocksModal
          onClose={() => setShowScan(false)}
          onScan={scanImage}
          onBulkAdd={bulkAdd}
        />
      )}
      {newsSymbol && (
        <StockNewsModal symbol={newsSymbol} onClose={() => setNewsSymbol(null)} />
      )}
    </div>
  );
}

export default StocksPage;
